import { Component } from "react";
import { useEffect, useCallback, useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import "@/App.css";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Passeios from "@/components/Passeios";
import JetskiMarketplace from "@/components/JetskiMarketplace";
import Clima from "@/components/Clima";
import Contato from "@/components/Contato";
import Footer from "@/components/Footer";
import MobileActions from "@/components/MobileActions";
import BookingPage from "@/pages/BookingPage";
import DemoBooking from "@/pages/DemoBooking";
import PolicyPage from "@/pages/PolicyPage";
import FAQ from "@/components/FAQ";
import Packages from "@/components/Packages";
import PaymentReturn from "@/pages/PaymentReturn";
import AdminLogin from "@/admin/AdminLogin";
import AdminApp from "@/admin/AdminApp";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-ocean text-ink">
                    <p>Algo deu errado. Recarregue a página.</p>
                </div>
            );
        }
        return this.props.children;
    }
}

function Landing() {
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    const scrollToId = useCallback((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (window.__lenis && !reduceMotion) window.__lenis.scrollTo(el, { offset: -70, duration: 0.85 });
        else el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    }, []);

    return (
        <div className={`grain relative overflow-x-clip bg-ocean font-body text-ink ${process.env.REACT_APP_PREVIEW_DEMO === "true" ? "pb-20 md:pb-0" : ""}`}>
            <Nav scrollToId={scrollToId} />
            <main>
                <Hero scrollToId={scrollToId} />
                <Marquee />
                {process.env.REACT_APP_PREVIEW_DEMO === "true" ? <section id="passeios"><JetskiMarketplace /></section> : <><Passeios /><Packages /></>}
                <FAQ />
                <Clima />
                <Contato />
            </main>
            <Footer />
            
        </div>
    );
}

/**
 * Transição entre telas públicas:
 * a página anterior desaparece suavemente antes de mostrar a próxima.
 * Apenas opacity é animada — sem transform no contêiner, para não afetar
 * barras e botões fixos do agendamento. Nenhuma biblioteca nova é instalada.
 */
function ScrollToTopOnArrival({ pathname }) {
    useLayoutEffect(() => {
        if (pathname === "/demonstracao" || pathname === "/") {
            if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
            window.scrollTo(0, 0);
        }
    }, [pathname]);
    return null;
}

function PageRoutes() {
    const location = useLocation();
    const reduceMotion = useReducedMotion();
    const isPublic = location.pathname === "/" || location.pathname === "/demonstracao"
        || location.pathname === "/politica-de-cancelamento" || location.pathname === "/privacidade";
    const animate = isPublic && !reduceMotion;
    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={location.pathname}
                initial={animate ? { opacity: 0 } : false}
                animate={{ opacity: 1 }}
                exit={animate ? { opacity: 0 } : { opacity: 1 }}
                transition={{ duration: animate ? 0.2 : 0, ease: "easeInOut" }}
                style={{ minHeight: "100vh", background: "var(--ocean)" }}
            >
                <ScrollToTopOnArrival pathname={location.pathname} />
                <Routes location={location}>
                    <Route path="/demonstracao" element={<DemoBooking />} />
                    <Route path="/pagamento/retorno" element={<PaymentReturn />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/*" element={<AdminApp />} />
                    <Route path="/reservar/:tourId" element={<BookingPage />} />
                    <Route path="/passeios/:tourId" element={<BookingPage />} />
                    <Route path="/politica-de-cancelamento" element={<PolicyPage key="cancelamento" sectionKey="cancelamento" />} />
                    <Route path="/privacidade" element={<PolicyPage key="privacidade" sectionKey="privacidade" />} />
                    <Route path="*" element={<Landing />} />
                </Routes>
            </motion.div>
        </AnimatePresence>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <ErrorBoundary>
                <PageRoutes />
                <MobileActions />
            </ErrorBoundary>
        </BrowserRouter>
    );
}
